import { Button, Card, Modal, Skeleton, cn } from '@pharmaconnect/ui/web'
import { formatCFA } from '@pharmaconnect/utils'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useTranslation } from '../i18n/I18nProvider'
import { useUserPreference } from '../hooks/useUserPreference'
import { supabase } from '../lib/supabase'
import { fetchInsurancePendingSummary } from '../services/insurance'
import { useAuthStore } from '../stores/authStore'

// Identifiants stables des cards pour les préférences user
type DashCardKey =
  | 'revenue_today'
  | 'sales_today'
  | 'low_stock'
  | 'imminent_stockouts'
  | 'deliveries'
  | 'overdue_credits'
  | 'pending_accounting'
  | 'insurance_claims'

const ALL_CARDS: { key: DashCardKey; label: string }[] = [
  { key: 'revenue_today', label: 'CA du jour' },
  { key: 'sales_today', label: 'Ventes du jour' },
  { key: 'low_stock', label: 'Stock bas' },
  { key: 'imminent_stockouts', label: 'Ruptures sous 7j' },
  { key: 'deliveries', label: 'Livraisons en cours' },
  { key: 'overdue_credits', label: 'Impayés en retard' },
  { key: 'pending_accounting', label: 'Écritures à valider' },
  { key: 'insurance_claims', label: 'Tiers payant à traiter' },
]

const DEFAULT_VISIBLE: Record<DashCardKey, boolean> = {
  revenue_today: true,
  sales_today: true,
  low_stock: true,
  imminent_stockouts: true,
  deliveries: true,
  overdue_credits: true,
  pending_accounting: true,
  insurance_claims: true,
}

export function DashboardPage() {
  const profile = useAuthStore((s) => s.profile)
  const pharmacyId = profile?.pharmacy_id ?? ''
  const { t } = useTranslation()
  const [configOpen, setConfigOpen] = useState(false)
  const { value: visibility, setValue: setVisibility } = useUserPreference<
    Record<DashCardKey, boolean>
  >('dashboard.cards_visibility', DEFAULT_VISIBLE)
  const isVisible = (key: DashCardKey): boolean => visibility[key] ?? true

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const todayISO = today.toISOString()

  // CA + nombre de ventes du jour
  const todaySales = useQuery({
    queryKey: ['dashboard', 'today-sales', pharmacyId],
    queryFn: async (): Promise<{ revenue: number; count: number }> => {
      const { data, error } = await supabase
        .from('sales')
        .select('total')
        .eq('pharmacy_id', pharmacyId)
        .eq('status', 'completed')
        .gte('created_at', todayISO)
      if (error) throw error
      const list = (data ?? []) as { total: number }[]
      return { revenue: list.reduce((sum, s) => sum + s.total, 0), count: list.length }
    },
    enabled: Boolean(pharmacyId),
    staleTime: 60_000,
  })

  // Ruptures imminentes (vague 14) — prédiction sur 7 jours via velocity
  const imminentStockouts = useQuery({
    queryKey: ['dashboard', 'imminent-stockouts', pharmacyId],
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc(
        'predict_stockouts' as never,
        { p_pharmacy_id: pharmacyId, p_threshold_days: 7 } as never,
      )
      if (error) throw error
      return ((data ?? []) as unknown[]).length
    },
    enabled: Boolean(pharmacyId),
    staleTime: 60_000,
  })

  // Médicaments en stock bas (quantity < alert_threshold) — fetch + filter client
  const lowStock = useQuery({
    queryKey: ['dashboard', 'low-stock', pharmacyId],
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase
        .from('medications')
        .select('id, quantity, alert_threshold')
        .eq('pharmacy_id', pharmacyId)
        .limit(500)
      if (error) throw error
      const meds = (data ?? []) as { quantity: number; alert_threshold: number }[]
      return meds.filter((m) => m.quantity < m.alert_threshold).length
    },
    enabled: Boolean(pharmacyId),
    staleTime: 60_000,
  })

  // Livraisons en cours (in_transit + assigned)
  const activeDeliveries = useQuery({
    queryKey: ['dashboard', 'active-deliveries', pharmacyId],
    queryFn: async (): Promise<number> => {
      const { count } = await supabase
        .from('delivery_orders')
        .select('id', { count: 'exact', head: true })
        .eq('pharmacy_id', pharmacyId)
        .in('status', ['assigned', 'in_transit'])
      return count ?? 0
    },
    enabled: Boolean(pharmacyId),
    staleTime: 30_000,
  })

  // Impayés en retard (vague 12)
  const overdueCredits = useQuery({
    queryKey: ['dashboard', 'overdue-credits', pharmacyId],
    queryFn: async (): Promise<{
      count_overdue: number
      total_amount_due: number
      max_days_overdue: number
    }> => {
      const { data, error } = await supabase.rpc(
        'get_overdue_credits_kpi' as never,
        { p_pharmacy_id: pharmacyId } as never,
      )
      if (error) throw error
      const row = (data as Array<{
        count_overdue: number
        total_amount_due: number
        max_days_overdue: number
      }> | null)?.[0]
      return {
        count_overdue: Number(row?.count_overdue ?? 0),
        total_amount_due: Number(row?.total_amount_due ?? 0),
        max_days_overdue: Number(row?.max_days_overdue ?? 0),
      }
    },
    enabled: Boolean(pharmacyId),
    staleTime: 60_000,
  })

  // V44 — Tiers payant : créances en attente
  const insuranceSummary = useQuery({
    queryKey: ['dashboard', 'insurance-summary', pharmacyId],
    queryFn: () => fetchInsurancePendingSummary(pharmacyId),
    enabled: Boolean(pharmacyId),
    staleTime: 60_000,
  })

  // Écritures comptables en attente
  const pendingEntries = useQuery({
    queryKey: ['dashboard', 'pending-entries', pharmacyId],
    queryFn: async (): Promise<number> => {
      const { count } = await supabase
        .from('accounting_entries')
        .select('id', { count: 'exact', head: true })
        .eq('pharmacy_id', pharmacyId)
        .eq('status', 'en_attente')
      return count ?? 0
    },
    enabled: Boolean(pharmacyId),
    staleTime: 60_000,
  })

  if (!pharmacyId) {
    return (
      <Card title={t('nav.dashboard')}>
        <p className="text-sm text-neutral-400">{t('dashboard.no_pharmacy')}</p>
      </Card>
    )
  }

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">
            Bonjour, {profile?.full_name?.split(' ')[0]} 👋
          </h2>
          <p className="text-sm text-neutral-400">{t('dashboard.title')}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfigOpen(true)}
          data-testid="open-dashboard-config"
        >
          {t('dashboard.customize_button')}
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
        {isVisible('revenue_today') ? (
          <KpiCard
            to="/caisse"
            label="CA du jour"
            loading={todaySales.isLoading}
            value={todaySales.data?.revenue}
            format="fcfa"
            tone="primary"
            testId="kpi-revenue-today"
          />
        ) : null}
        {isVisible('sales_today') ? (
          <KpiCard
            to="/caisse"
            label="Ventes du jour"
            loading={todaySales.isLoading}
            value={todaySales.data?.count}
            tone="neutral"
            testId="kpi-sales-today"
          />
        ) : null}
        {isVisible('low_stock') ? (
          <KpiCard
            to="/stocks"
            label="Stock bas"
            loading={lowStock.isLoading}
            value={lowStock.data}
            tone={(lowStock.data ?? 0) > 0 ? 'danger' : 'success'}
            testId="kpi-low-stock"
          />
        ) : null}
        {isVisible('imminent_stockouts') ? (
          <KpiCard
            to="/reappro"
            label="Ruptures sous 7j"
            loading={imminentStockouts.isLoading}
            value={imminentStockouts.data}
            tone={(imminentStockouts.data ?? 0) > 0 ? 'warning' : 'success'}
            testId="kpi-imminent-stockouts"
          />
        ) : null}
        {isVisible('deliveries') ? (
          <KpiCard
            to="/livraisons"
            label="Livraisons en cours"
            loading={activeDeliveries.isLoading}
            value={activeDeliveries.data}
            tone="primary"
            testId="kpi-deliveries"
          />
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {isVisible('overdue_credits') ? (
          <Card
            title="Impayés en retard"
            subtitle="Crédits clients dont l'échéance est dépassée"
          >
            {overdueCredits.isLoading ? (
              <Skeleton height="h-7" />
            ) : (overdueCredits.data?.count_overdue ?? 0) === 0 ? (
              <p className="text-sm text-neutral-400">Aucun impayé en retard. ✨</p>
            ) : (
              <div
                className="flex items-center justify-between"
                data-testid="overdue-credits-block"
              >
                <div>
                  <p className="font-mono text-3xl font-bold text-offline">
                    {formatCFA(overdueCredits.data?.total_amount_due ?? 0)}
                  </p>
                  <p className="text-xs text-neutral-400">
                    {overdueCredits.data?.count_overdue} crédit
                    {(overdueCredits.data?.count_overdue ?? 0) > 1 ? 's' : ''} · max{' '}
                    {overdueCredits.data?.max_days_overdue} j de retard
                  </p>
                </div>
                <Link to="/credits" className="text-sm text-pharmacist hover:underline">
                  Voir les crédits →
                </Link>
              </div>
            )}
          </Card>
        ) : null}

        {isVisible('pending_accounting') ? (
          <Card title="Comptabilité" subtitle="Écritures en attente de validation">
            {pendingEntries.isLoading ? (
              <Skeleton height="h-7" />
            ) : (pendingEntries.data ?? 0) === 0 ? (
              <p className="text-sm text-neutral-400">Aucune écriture en attente. ✨</p>
            ) : (
              <div className="flex items-center justify-between">
                <p className="font-mono text-3xl font-bold text-amber-400">
                  {pendingEntries.data}
                </p>
                <Link to="/comptabilite" className="text-sm text-pharmacist hover:underline">
                  Aller valider →
                </Link>
              </div>
            )}
          </Card>
        ) : null}

        {isVisible('insurance_claims') ? (
          <Card
            title={t('nav.insurance_claims')}
            subtitle="Créances assurance en attente de soumission ou remboursement"
          >
            {insuranceSummary.isLoading ? (
              <Skeleton height="h-7" />
            ) : (insuranceSummary.data?.count_pending ?? 0) +
                (insuranceSummary.data?.count_submitted ?? 0) ===
              0 ? (
              <p className="text-sm text-neutral-400">Aucune créance en cours. ✨</p>
            ) : (
              <div
                className="flex items-center justify-between"
                data-testid="insurance-claims-block"
              >
                <div>
                  <p className="font-mono text-3xl font-bold text-info">
                    {formatCFA(
                      (insuranceSummary.data?.total_pending ?? 0) +
                        (insuranceSummary.data?.total_submitted ?? 0),
                    )}
                  </p>
                  <p className="text-xs text-neutral-400">
                    {insuranceSummary.data?.count_pending ?? 0} à soumettre ·{' '}
                    {insuranceSummary.data?.count_submitted ?? 0} en attente
                  </p>
                </div>
                <Link to="/tiers-payant" className="text-sm text-pharmacist hover:underline">
                  Gérer →
                </Link>
              </div>
            )}
          </Card>
        ) : null}

        <Card title="Raccourcis">
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/caisse"
              className="rounded-md border border-pharmacist/30 bg-pharmacist/10 p-3 text-center text-sm text-pharmacist hover:bg-pharmacist/20"
            >
              Encaisser
            </Link>
            <Link
              to="/stocks"
              className="rounded-md border border-white/10 bg-white/5 p-3 text-center text-sm text-white hover:bg-white/10"
            >
              Ajouter stock
            </Link>
            <Link
              to="/comptabilite"
              className="rounded-md border border-white/10 bg-white/5 p-3 text-center text-sm text-white hover:bg-white/10"
            >
              Compta
            </Link>
            <Link
              to="/livraisons"
              className="rounded-md border border-white/10 bg-white/5 p-3 text-center text-sm text-white hover:bg-white/10"
            >
              Livraisons
            </Link>
          </div>
        </Card>
      </div>
      <Modal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        size="md"
        title={t('dashboard.customize_title')}
        description={t('dashboard.customize_description')}
        footer={
          <Button variant="primary" onClick={() => setConfigOpen(false)}>
            {t('common.close')}
          </Button>
        }
      >
        <ul className="flex flex-col gap-2">
          {ALL_CARDS.map((c) => (
            <li
              key={c.key}
              className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.02] p-3"
            >
              <span className="text-sm text-white">{c.label}</span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={isVisible(c.key)}
                  onChange={(e) =>
                    setVisibility({ ...visibility, [c.key]: e.target.checked })
                  }
                  className="peer h-5 w-9 cursor-pointer appearance-none rounded-full bg-neutral-700 transition checked:bg-pharmacist"
                  data-testid={`toggle-${c.key}`}
                />
                <span className="pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-4" />
              </label>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-neutral-500">
          Tip : masquer une card ne supprime pas les données — réactivez-la quand vous voulez.
        </p>
      </Modal>
    </section>
  )
}

interface KpiCardProps {
  to: string
  label: string
  value: number | undefined
  loading: boolean
  format?: 'fcfa' | 'count'
  tone: 'success' | 'danger' | 'warning' | 'primary' | 'neutral'
  testId: string
}

function KpiCard({ to, label, value, loading, format, tone, testId }: KpiCardProps) {
  return (
    <Link
      to={to}
      className="block rounded-lg border border-white/10 bg-white/[0.02] p-4 transition hover:border-white/20"
    >
      <p className="text-xs uppercase tracking-wider text-neutral-400">{label}</p>
      {loading ? (
        <Skeleton height="h-7" width="w-3/4" />
      ) : (
        <p
          className={cn(
            'mt-1 font-mono text-3xl font-bold',
            tone === 'success' && 'text-online',
            tone === 'danger' && 'text-offline',
            tone === 'warning' && 'text-amber-400',
            tone === 'primary' && 'text-pharmacist',
            tone === 'neutral' && 'text-white',
          )}
          data-testid={testId}
        >
          {format === 'fcfa' ? formatCFA(value ?? 0) : (value ?? 0)}
        </p>
      )}
    </Link>
  )
}
